import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { FiArrowRight, FiCheckCircle, FiClock, FiAward } from 'react-icons/fi';

const Section = styled.section`
  padding: 4rem 0;
  background: white;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grain" width="100" height="100" patternUnits="userSpaceOnUse"><circle cx="25" cy="25" r="1" fill="%2301675f" opacity="0.05"/><circle cx="75" cy="75" r="1" fill="%2301675f" opacity="0.05"/><circle cx="50" cy="10" r="0.5" fill="%2301675f" opacity="0.03"/><circle cx="10" cy="60" r="0.5" fill="%2301675f" opacity="0.03"/><circle cx="90" cy="40" r="0.5" fill="%2301675f" opacity="0.03"/></pattern></defs><rect width="100" height="100" fill="url(%23grain)"/></svg>');
    opacity: 0.3;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  position: relative;
  z-index: 1;
`;

const Content = styled.div`
  text-align: center;
  color: ${props => props.theme.colors.heading};
`;

const Title = styled.h2`
  font-size: ${props => props.theme.fontSizes['4xl']};
  font-weight: ${props => props.theme.fontWeights.bold};
  margin-bottom: 1.5rem;
  color: ${props => props.theme.colors.heading};
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    font-size: ${props => props.theme.fontSizes['3xl']};
  }
`;

const Subtitle = styled.p`
  font-size: ${props => props.theme.fontSizes.xl};
  margin-bottom: 3rem;
  opacity: 0.8;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  color: ${props => props.theme.colors.textSecondary};
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    font-size: ${props => props.theme.fontSizes.lg};
  }
`;

const BenefitsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
  margin-bottom: 3rem;
`;

const BenefitCard = styled.div`
  background: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.divider};
  padding: 2rem;
  border-radius: ${props => props.theme.borderRadius.xl};
  transition: ${props => props.theme.transitions.base};
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
    border-color: ${props => props.theme.colors.primary};
  }
`;

const BenefitIcon = styled.div`
  width: 60px;
  height: 60px;
  background: ${props => props.theme.colors.primary};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem;
  color: white;
  font-size: 1.5rem;
`;

const BenefitTitle = styled.h3`
  font-size: ${props => props.theme.fontSizes.lg};
  font-weight: ${props => props.theme.fontWeights.semibold};
  margin-bottom: 0.5rem;
  color: ${props => props.theme.colors.heading};
`;

const BenefitDescription = styled.p`
  font-size: ${props => props.theme.fontSizes.base};
  opacity: 0.8;
  line-height: 1.6;
  color: ${props => props.theme.colors.textSecondary};
`;

const CTAButtons = styled.div`
  display: flex;
  gap: 1.5rem;
  justify-content: center;
  flex-wrap: wrap;
`;

const PrimaryButton = styled(Link)`
  background: ${props => props.theme.colors.primary};
  color: white;
  padding: 1rem 2rem;
  border-radius: ${props => props.theme.borderRadius.lg};
  text-decoration: none;
  font-weight: ${props => props.theme.fontWeights.semibold};
  font-size: ${props => props.theme.fontSizes.lg};
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  transition: ${props => props.theme.transitions.base};
  box-shadow: 0 4px 12px rgba(1, 103, 95, 0.3);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(1, 103, 95, 0.4);
    color: white;
  }
`;

const SecondaryButton = styled(Link)`
  background: transparent;
  color: ${props => props.theme.colors.primary};
  padding: 1rem 2rem;
  border-radius: ${props => props.theme.borderRadius.lg};
  text-decoration: none;
  font-weight: ${props => props.theme.fontWeights.semibold};
  font-size: ${props => props.theme.fontSizes.lg};
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  transition: ${props => props.theme.transitions.base};
  border: 2px solid ${props => props.theme.colors.primary};
  
  &:hover {
    background: ${props => props.theme.colors.primary};
    color: white;
  }
`;

const CourseCTASection = ({ courseDetail }) => {
  if (!courseDetail) return null;

  return (
    <Section>
      <Container>
        <Content>
          <Title>Ready to Get Your {courseDetail.courseTitle}?</Title>
          <Subtitle>
            Take the first step towards your qualification through our streamlined RPL process
          </Subtitle>
          
          <BenefitsGrid>
            <BenefitCard>
              <BenefitIcon>
                <FiCheckCircle />
              </BenefitIcon>
              <BenefitTitle>Fast Assessment</BenefitTitle>
              <BenefitDescription>
                Get your qualification recognized quickly with our efficient RPL process
              </BenefitDescription>
            </BenefitCard>
            
            <BenefitCard>
              <BenefitIcon>
                <FiClock />
              </BenefitIcon>
              <BenefitTitle>100% Online</BenefitTitle>
              <BenefitDescription>
                Complete your entire RPL assessment online at your own pace
              </BenefitDescription>
            </BenefitCard>
            
            <BenefitCard>
              <BenefitIcon>
                <FiAward />
              </BenefitIcon>
              <BenefitTitle>Nationally Recognized</BenefitTitle>
              <BenefitDescription>
                Receive a nationally recognized qualification that's accepted across Australia
              </BenefitDescription>
            </BenefitCard>
          </BenefitsGrid>
          
          <CTAButtons>
            <PrimaryButton to="/free-quote">
              Start Your RPL Assessment
              <FiArrowRight />
            </PrimaryButton>
            <SecondaryButton to="/how-rpl-works">
              Learn More About RPL
              <FiArrowRight />
            </SecondaryButton>
          </CTAButtons>
        </Content>
      </Container>
    </Section>
  );
};

export default CourseCTASection; 